import {
  useResendBookingConfirmation,
  useResendBookingContract,
  useResendUpdatedBookingContractImmediately,
  useSendRentalKeyLink,
} from '@/queries/use-bookings';
import { Alert } from '@/services/alert.service';
import { useI18n } from 'vue-i18n';

export const useResendBookingMails = (bookingId: string) => {
  const { t } = useI18n({
    useScope: 'global',
    legacy: false,
    fallbackLocale: 'en',
    globalInjection: true,
    fallbackWarn: false,
    missingWarn: false,
    messages: localization,
  });

  const { mutateAsync: resendBookingConfirmation } = useResendBookingConfirmation();
  const { mutateAsync: resendBookingContract } = useResendBookingContract();
  const { mutateAsync: resendUpdatedBookingContractImmediately } =
    useResendUpdatedBookingContractImmediately();
  const { mutateAsync: sendRentalKeyLinkMail } = useSendRentalKeyLink();

  const resendConfirmationEmail = async (skipConfirmation?: boolean) => {
    if (
      skipConfirmation ||
      (
        await Alert.fire({
          titleText: t('reallySendConfirmationTitle'),
          text: t('reallySendConfirmationText'),
          icon: 'question',
          showDenyButton: true,
          confirmButtonText: t('sendConfirmation'),
          denyButtonText: t('cancel'),
        })
      ).isConfirmed
    ) {
      resendBookingConfirmation(bookingId);
    }
  };

  const resendContractEmail = async () => {
    const alertResult = await Alert.fire({
      titleText: t('reallySendContractTitle'),
      text: t('reallySendContractText'),
      icon: 'question',
      showDenyButton: true,
      confirmButtonText: t('sendContract'),
      denyButtonText: t('cancel'),
    });
    if (alertResult.isConfirmed) resendBookingContract(bookingId);
  };

  const resendUpdatedContractImmediately = async () => {
    const alertResult = await Alert.fire({
      titleText: t('reallySendUpdatedContractNowTitle'),
      text: t('reallySendUpdatedContractNowText'),
      icon: 'question',
      showDenyButton: true,
      confirmButtonText: t('sendUpdatedContractNow'),
      denyButtonText: t('cancel'),
    });
    if (alertResult.isConfirmed) resendUpdatedBookingContractImmediately(bookingId);
  };

  const sendRentalKeyLink = async () => {
    const { value, isConfirmed } = await Alert.fire({
      titleText: t('reallySendRentalKeyLinkTitle'),
      text: t('reallySendRentalKeyLinkText'),
      icon: 'question',
      showDenyButton: true,
      confirmButtonText: t('createRentalKeyLink'),
      denyButtonText: t('cancel'),
      input: 'select',
      inputOptions: {
        mail: t('sendRentalKeyLinkByMail'),
        copy: t('copyRentalKeyLink'),
      },
      inputValue: 'mail',
    });
    if (!isConfirmed) return;
    const sendMail = value === 'mail';
    const { rentalKeyLink } = await sendRentalKeyLinkMail({ bookingId, sendMail });
    if (!sendMail) {
      await showRentalKeyLinkToCopy(rentalKeyLink, t);
    }
  };

  return {
    resendConfirmationEmail,
    resendContractEmail,
    resendUpdatedContractImmediately,
    sendRentalKeyLink,
  };
};

const showRentalKeyLinkToCopy = async (rentalKeyLink: string, t: (key: string) => string) => {
  let couldNotCopy = false;
  try {
    await navigator.clipboard.writeText(rentalKeyLink);
  } catch {
    couldNotCopy = true;
  }
  await Alert.fire({
    html: couldNotCopy ? t('couldNotCopyRentalKeyLinkText') : t('copyThisRentalKeyLinkText'),
    icon: 'success',
    confirmButtonText: t('close'),
    input: 'text',
    inputValue: rentalKeyLink,
    inputAttributes: { readonly: 'readonly' },
    showInputCopyButton: true,
  });
};

const localization = {
  en: {
    reallySendContractTitle: 'Really send Contract?',
    reallySendContractText: 'Are you sure, you want to send this Confirmation to the Customer?',
    sendContract: 'Send Contract',
    reallySendUpdatedContractNowTitle: 'Regenerate and send updated contract now?',
    reallySendUpdatedContractNowText:
      'This will regenerate the updated contract and send it immediately, bypassing the debounce delay.',
    sendUpdatedContractNow: 'Send updated contract now',
    reallySendConfirmationTitle: 'Really send Confirmation?',
    reallySendConfirmationText: 'Are you sure, you want to send this Confirmation to the Customer?',
    sendConfirmation: 'Send Confirmation',
    reallySendRentalKeyLinkTitle: 'Rental overview link',
    reallySendRentalKeyLinkText: 'The link is valid for 24 hours.',
    sendRentalKeyLinkByMail: 'Send by email',
    copyRentalKeyLink: 'Copy link',
    createRentalKeyLink: 'Create link',
    copyThisRentalKeyLinkText:
      'Here is the rental overview link.<br><strong>It was already automatically copied to the clipboard.</strong>',
    couldNotCopyRentalKeyLinkText:
      'Here is the rental overview link.<br><strong>It could not be copied to the clipboard.<br>Please copy it manually.</strong>',
    close: 'Close',
    cancel: 'Cancel',
  },
  de: {
    reallySendContractTitle: 'Vertrag wirklich versenden?',
    reallySendContractText:
      'Bist du sicher, dass du diesen Vertrag an den Kunden versenden möchtest?',
    sendContract: 'Vertrag verschicken',
    reallySendUpdatedContractNowTitle: 'Aktualisierten Vertrag jetzt neu erstellen und senden?',
    reallySendUpdatedContractNowText:
      'Dadurch wird der aktualisierte Vertrag sofort neu erstellt und gesendet, ohne Debounce-Verzoegerung.',
    sendUpdatedContractNow: 'Aktualisierten Vertrag jetzt senden',
    reallySendConfirmationTitle: 'Bestätigung wirklich versenden?',
    reallySendConfirmationText:
      'Bist du sicher, dass du diese Bestätigung an den Kunden versenden möchtest?',
    sendConfirmation: 'Bestätigung verschicken',
    reallySendRentalKeyLinkTitle: 'Mietübersicht-Link',
    reallySendRentalKeyLinkText: 'Der Link ist 24 Stunden gültig.',
    sendRentalKeyLinkByMail: 'Per E-Mail senden',
    copyRentalKeyLink: 'Link kopieren',
    createRentalKeyLink: 'Link erstellen',
    copyThisRentalKeyLinkText:
      'Hier ist der Link zur Mietübersicht.<br><strong>Er wurde bereits automatisch in die Zwischenablage kopiert.</strong>',
    couldNotCopyRentalKeyLinkText:
      'Hier ist der Link zur Mietübersicht.<br><strong>Er konnte nicht in die Zwischenablage kopiert werden.<br>Bitte manuell kopieren.</strong>',
    close: 'Schließen',
    cancel: 'Abbrechen',
  },
};
